import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import TasksViewTabs from "../../tasksShared/TasksViewTabs";
import TaskWorkspaceHeader from "../../tasksShared/TaskWorkspaceHeader";
import CalendarTaskEditorModal from "../../tasksShared/widgets/TaskEditorModal";
import CalendarTaskDeleteDialog from "../../tasksCalendar/widgets/CalendarTaskDeleteDialog";
import { deleteCalendarTask } from "../../tasksShared/api/taskMutations";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { totalPagesFromCount } from "../../../shared/ui/Pagination/paginationModel";
import { messageFromError } from "../../../shared/api/drfErrors";

import { fetchClientSummary, fetchTasks } from "../api/tasks";
import { fetchPropertySummary } from "../api/properties";
import { createPropertySummaryStore } from "../lib/propertySummaryLoader";
import {
  buildApiParams,
  buildUrlSearchParams,
  datetimeLocalFromIso,
  filtersWithPageReset,
  hasActiveFilters,
  isoFromDatetimeLocal,
  parseFiltersFromSearchParams,
  PRIORITY_CHIPS,
  STATUS_CHIPS,
  toggleSetValue,
} from "../lib/taskListQuery";
import { TASK_LIST_PAGE_SIZE } from "../lib/taskListPagination";
import {
  clientDisplayName,
  formatCreatedDateUk,
  formatDateTime,
  getPriorityLabel,
  getStatusLabel,
  isTaskOverdue,
  propertyTitleOrFallback,
  userDisplayName,
} from "../lib/taskFormatting";

function todayLocalDate() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function PropertySummaryCell({ propertyId, summaryEntry }) {
  if (!propertyId) {
    return <span>—</span>;
  }

  if (!summaryEntry || summaryEntry.status === "error") {
    return (
      <div className="feature-tasks-list__property-fallback">
        <Link to={`/realty/${propertyId}`} className="feature-tasks-list__link">
          {propertyTitleOrFallback(null, propertyId)}
        </Link>
        {summaryEntry && summaryEntry.status === "error" ? (
          <span className="feature-tasks-list__property-fallback-note">
            Дані про об&apos;єкт недоступні
          </span>
        ) : null}
      </div>
    );
  }

  const property = summaryEntry.data;
  return (
    <Link to={`/realty/${propertyId}`} className="feature-tasks-list__link">
      {propertyTitleOrFallback(property, propertyId)}
    </Link>
  );
}

function ContextChip({ label, onRemove, ariaLabel }) {
  return (
    <span className="filter-chip filter-chip--active feature-tasks-list__context-chip">
      <span>{label}</span>
      <button
        type="button"
        className="feature-tasks-list__context-chip-remove"
        onClick={onRemove}
        aria-label={ariaLabel}
      >
        <i className="fa-solid fa-xmark" aria-hidden="true" />
      </button>
    </span>
  );
}

function TaskRowActionsMenu({
  task,
  menuOpen,
  onToggleMenu,
  onOpenEdit,
  onRequestDelete,
  triggerRef,
}) {
  return (
    <div className="feature-tasks-list__row-menu">
      <button
        ref={triggerRef}
        type="button"
        className="feature-tasks-list__menu-trigger"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Дії для задачі «${task.title}»`}
        onClick={() => onToggleMenu(task.id)}
      >
        <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
      </button>
      {menuOpen ? (
        <div
          className="feature-tasks-list__menu"
          role="menu"
          aria-label={`Меню задачі «${task.title}»`}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => onOpenEdit(task)}
          >
            Редагувати
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => onRequestDelete(task)}
          >
            Видалити
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TasksListScreenContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const propertySummaryStoreRef = useRef(null);
  if (!propertySummaryStoreRef.current) {
    propertySummaryStoreRef.current = createPropertySummaryStore();
  }
  const filters = useMemo(
    () => parseFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [propertySummaries, setPropertySummaries] = useState(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [clientContextLabel, setClientContextLabel] = useState(null);
  const [propertyContextLabel, setPropertyContextLabel] = useState(null);
  const [editorState, setEditorState] = useState({
    open: false,
    mode: "create",
    task: null,
    propertyLabel: "",
  });
  const [deleteState, setDeleteState] = useState({
    open: false,
    task: null,
    pending: false,
    error: null,
  });
  const [openMenuTaskId, setOpenMenuTaskId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const mutationGenerationRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const activeRequestControllerRef = useRef(null);
  const pendingMutationsRef = useRef(new Set());
  const menuTriggerRefs = useRef(new Map());
  const restoreMenuFocusTaskIdRef = useRef(null);

  const activeFilters = hasActiveFilters(filters);
  const dueAfterLocal = datetimeLocalFromIso(filters.dueAfterIso);
  const dueBeforeLocal = datetimeLocalFromIso(filters.dueBeforeIso);

  const applyFilters = useCallback(
    (nextFilters) => {
      setSearchParams(buildUrlSearchParams(nextFilters));
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const goToPage = useCallback(
    (page) => {
      applyFilters({ ...filters, page });
    },
    [applyFilters, filters],
  );

  const beginListRequest = useCallback(() => {
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    return controller;
  }, []);

  const clearListRequest = useCallback((controller) => {
    if (activeRequestControllerRef.current === controller) {
      activeRequestControllerRef.current = null;
    }
  }, []);

  const loadTasks = useCallback(
    async ({ signal, mode = "initial", generation } = {}) => {
      const propertyStore = propertySummaryStoreRef.current;
      const isCurrentGeneration = () =>
        generation == null || generation === loadGenerationRef.current;

      if (filters.reversedDateRange || filters.invalidDateParams) {
        if (mode === "initial" && isCurrentGeneration()) {
          setLoadState((prev) => (prev === "loading" ? "loaded" : prev));
        }
        return { ok: true };
      }

      if (mode === "initial") {
        setLoadState("loading");
        setLoadError(null);
        setTasks([]);
        setPagination({
          count: 0,
          next: null,
          previous: null,
        });
        setPropertySummaries(new Map());
      }

      try {
        const payload = await fetchTasks(buildApiParams(filters), { signal });
        if (signal && signal.aborted) return { ok: false, aborted: true };
        if (!isCurrentGeneration()) return { ok: false, stale: true };

        const pageTasks = Array.isArray(payload.results) ? payload.results : [];
        propertyStore.reset();
        setTasks(pageTasks);
        setPagination({
          count: payload.count || 0,
          next: payload.next || null,
          previous: payload.previous || null,
        });

        const summaries = await propertyStore.loadForTasks(
          pageTasks,
          (propertyId) => fetchPropertySummary(propertyId, { signal }),
          { signal },
        );
        if (signal && signal.aborted) return { ok: false, aborted: true };
        if (!isCurrentGeneration()) return { ok: false, stale: true };

        setPropertySummaries(summaries);
        setLoadState("loaded");
        setRefreshError(null);
        return { ok: true };
      } catch (error) {
        if (signal && signal.aborted) return { ok: false, aborted: true };
        if (!isCurrentGeneration()) return { ok: false, stale: true };
        const message = messageFromError(error);
        if (mode === "refresh") {
          setRefreshError(message);
          return { ok: false };
        }
        setTasks([]);
        setPagination({
          count: 0,
          next: null,
          previous: null,
        });
        setPropertySummaries(new Map());
        setLoadError(message);
        setLoadState("error");
        return { ok: false };
      }
    },
    [filters],
  );

  useEffect(
    () => () => {
      if (activeRequestControllerRef.current) {
        activeRequestControllerRef.current.abort();
        activeRequestControllerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    mutationGenerationRef.current += 1;
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginListRequest();
    loadTasks({
      signal: controller.signal,
      mode: "initial",
      generation,
    }).finally(() => {
      clearListRequest(controller);
    });
    return () => {
      controller.abort();
      clearListRequest(controller);
    };
  }, [loadTasks, beginListRequest, clearListRequest]);

  useEffect(() => {
    if (filters.clientId == null) {
      setClientContextLabel(null);
      return undefined;
    }

    const nestedClient = tasks.find(
      (task) => task.client && task.client.id === filters.clientId,
    )?.client;
    if (nestedClient) {
      setClientContextLabel(clientDisplayName(nestedClient, filters.clientId));
      return undefined;
    }

    const controller = new AbortController();
    fetchClientSummary(filters.clientId, { signal: controller.signal })
      .then((client) => {
        if (controller.signal.aborted) return;
        setClientContextLabel(clientDisplayName(client, filters.clientId));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setClientContextLabel(clientDisplayName(null, filters.clientId));
      });

    return () => controller.abort();
  }, [filters.clientId, tasks]);

  useEffect(() => {
    if (filters.propertyId == null) {
      setPropertyContextLabel(null);
      return undefined;
    }

    const propertyStore = propertySummaryStoreRef.current;
    const summary = propertyStore.get(filters.propertyId);
    if (summary && summary.status === "loaded") {
      setPropertyContextLabel(
        propertyTitleOrFallback(summary.data, filters.propertyId),
      );
      return undefined;
    }
    if (summary && summary.status === "error") {
      setPropertyContextLabel(propertyTitleOrFallback(null, filters.propertyId));
      return undefined;
    }

    if (loadState === "loading") {
      return undefined;
    }

    const controller = new AbortController();
    propertyStore
      .loadProperty(
        filters.propertyId,
        (propertyId) => fetchPropertySummary(propertyId, { signal: controller.signal }),
        { signal: controller.signal },
      )
      .then((entry) => {
        if (controller.signal.aborted) return;
        if (entry.status === "loaded") {
          setPropertyContextLabel(
            propertyTitleOrFallback(entry.data, filters.propertyId),
          );
          setPropertySummaries((prev) => {
            const next = new Map(prev);
            next.set(filters.propertyId, entry);
            return next;
          });
          return;
        }
        setPropertyContextLabel(propertyTitleOrFallback(null, filters.propertyId));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setPropertyContextLabel(propertyTitleOrFallback(null, filters.propertyId));
      });

    return () => controller.abort();
  }, [filters.propertyId, tasks, loadState]);

  useEffect(() => {
    if (openMenuTaskId == null) return undefined;

    const handlePointer = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".feature-tasks-list__row-menu")) return;
      restoreMenuFocusTaskIdRef.current = openMenuTaskId;
      setOpenMenuTaskId(null);
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      restoreMenuFocusTaskIdRef.current = openMenuTaskId;
      setOpenMenuTaskId(null);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuTaskId]);

  useEffect(() => {
    if (openMenuTaskId != null) return;
    const restoreId = restoreMenuFocusTaskIdRef.current;
    if (restoreId == null) return;
    restoreMenuFocusTaskIdRef.current = null;
    const trigger = menuTriggerRefs.current.get(restoreId);
    if (trigger && typeof trigger.focus === "function") {
      trigger.focus();
    }
  }, [openMenuTaskId]);

  const runBoundedRefresh = useCallback(async () => {
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginListRequest();
    setRefreshing(true);
    setRefreshError(null);
    try {
      await loadTasks({
        signal: controller.signal,
        mode: "refresh",
        generation,
      });
    } finally {
      clearListRequest(controller);
      if (generation === loadGenerationRef.current) {
        setRefreshing(false);
      }
    }
  }, [beginListRequest, clearListRequest, loadTasks]);

  const handleRefresh = () => {
    if (refreshing) return;
    runBoundedRefresh();
  };

  const handleRetry = () => {
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const controller = beginListRequest();
    loadTasks({
      signal: controller.signal,
      mode: "initial",
      generation,
    }).finally(() => {
      clearListRequest(controller);
    });
  };

  const openCreateEditor = () => {
    setOpenMenuTaskId(null);
    setEditorState({
      open: true,
      mode: "create",
      task: null,
      propertyLabel: "",
    });
  };

  const openEditEditor = (task) => {
    setOpenMenuTaskId(null);
    let propertyLabel = "";
    if (task.property != null) {
      const summaryEntry = propertySummaries.get(task.property);
      propertyLabel =
        summaryEntry && summaryEntry.status === "loaded"
          ? propertyTitleOrFallback(summaryEntry.data, task.property)
          : propertyTitleOrFallback(null, task.property);
    }
    setEditorState({
      open: true,
      mode: "edit",
      task,
      propertyLabel,
    });
  };

  const closeEditor = () => {
    setEditorState({
      open: false,
      mode: "create",
      task: null,
      propertyLabel: "",
    });
  };

  const handleEditorSaved = async (saved, meta = {}) => {
    const generationAtSave = mutationGenerationRef.current;
    const savedMode = meta.mode || editorState.mode || "create";
    closeEditor();
    if (generationAtSave !== mutationGenerationRef.current) return;
    setStatusMessage(
      savedMode === "edit" ? "Задачу оновлено." : "Задачу створено.",
    );
    // Always replace any pending refresh so post-save reconciliation is not skipped.
    await runBoundedRefresh();
  };

  const handleRequestDelete = (task) => {
    setOpenMenuTaskId(null);
    setDeleteState({
      open: true,
      task,
      pending: false,
      error: null,
    });
  };

  const handleCancelDelete = () => {
    if (deleteState.pending) return;
    setDeleteState({
      open: false,
      task: null,
      pending: false,
      error: null,
    });
  };

  const handleConfirmDelete = async () => {
    const task = deleteState.task;
    if (!task || deleteState.pending) return;
    if (pendingMutationsRef.current.has(`delete-${task.id}`)) return;

    const generationAtStart = mutationGenerationRef.current;
    pendingMutationsRef.current.add(`delete-${task.id}`);
    setDeleteState((prev) => ({ ...prev, pending: true, error: null }));

    try {
      await deleteCalendarTask(task.id);
      if (generationAtStart !== mutationGenerationRef.current) {
        setDeleteState({
          open: false,
          task: null,
          pending: false,
          error: null,
        });
        return;
      }
      setTasks((prev) => prev.filter((row) => row.id !== task.id));
      setPagination((prev) => ({
        ...prev,
        count: Math.max(0, (prev.count || 0) - 1),
      }));
      setDeleteState({
        open: false,
        task: null,
        pending: false,
        error: null,
      });
      setStatusMessage("Задачу видалено.");
    } catch (err) {
      if (generationAtStart !== mutationGenerationRef.current) return;
      setDeleteState((prev) => ({
        ...prev,
        pending: false,
        error: messageFromError(err) || "Не вдалося видалити задачу.",
      }));
    } finally {
      pendingMutationsRef.current.delete(`delete-${task.id}`);
    }
  };

  const handleToggleMenu = (taskId) => {
    setOpenMenuTaskId((prev) => {
      if (prev === taskId) {
        restoreMenuFocusTaskIdRef.current = taskId;
        return null;
      }
      restoreMenuFocusTaskIdRef.current = null;
      return taskId;
    });
  };

  const setMenuTriggerRef = (taskId, node) => {
    if (node) {
      menuTriggerRefs.current.set(taskId, node);
    } else {
      menuTriggerRefs.current.delete(taskId);
    }
  };

  const handleStatusToggle = (status) => {
    applyFilters(
      filtersWithPageReset(filters, {
        statuses: toggleSetValue(filters.statuses, status),
      }),
    );
  };

  const handlePriorityToggle = (priority) => {
    applyFilters(
      filtersWithPageReset(filters, {
        priorities: toggleSetValue(filters.priorities, priority),
      }),
    );
  };

  const handleOverdueToggle = () => {
    applyFilters(
      filtersWithPageReset(filters, {
        overdue: !filters.overdue,
      }),
    );
  };

  const handleDueAfterChange = (event) => {
    const iso = isoFromDatetimeLocal(event.target.value);
    applyFilters(
      filtersWithPageReset(filters, {
        dueAfterIso: iso,
      }),
    );
  };

  const handleDueBeforeChange = (event) => {
    const iso = isoFromDatetimeLocal(event.target.value);
    applyFilters(
      filtersWithPageReset(filters, {
        dueBeforeIso: iso,
      }),
    );
  };

  const removeClientContext = () => {
    applyFilters(filtersWithPageReset(filters, { clientId: null }));
  };

  const removePropertyContext = () => {
    applyFilters(filtersWithPageReset(filters, { propertyId: null }));
  };

  const showInitialLoading = loadState === "loading" && tasks.length === 0;
  const showPrimaryError = loadState === "error";
  const showTable =
    loadState === "loaded" &&
    tasks.length > 0 &&
    !filters.reversedDateRange &&
    !filters.invalidDateParams;
  const showFilteredEmpty =
    loadState === "loaded" &&
    tasks.length === 0 &&
    activeFilters &&
    !filters.reversedDateRange &&
    !filters.invalidDateParams;
  const showUnfilteredEmpty =
    loadState === "loaded" &&
    tasks.length === 0 &&
    !activeFilters &&
    !filters.reversedDateRange &&
    !filters.invalidDateParams;

  return (
    <div className="feature-tasks-list" data-feature="tasks-list">
      <TaskWorkspaceHeader
        subtitle="Задачі, які ви створили або які призначені вам."
        onCreate={openCreateEditor}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshDisabled={loadState === "loading"}
      />

      <TasksViewTabs />

      <div
        className="feature-tasks-list__live"
        role="status"
        aria-live="polite"
      >
        {statusMessage}
      </div>

      {refreshError ? (
        <div className="feature-tasks-list__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      <div className="clients-workspace" data-feature="tasks-list-filters">
        <div className="clients-workspace__filters">
          <div
            className="clients-toolbar"
            role="search"
            aria-label="Фільтри списку задач"
          >
            <div className="clients-toolbar__row">
              <div
                className="clients-filter-group"
                role="group"
                aria-label="Статус"
              >
                <span className="clients-filter-group__title">Статус</span>
                {STATUS_CHIPS.map((chip) => {
                  const on = filters.statuses.includes(chip.value);
                  return (
                    <label
                      key={chip.value}
                      className="filter-chip"
                      data-on={on ? "" : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => handleStatusToggle(chip.value)}
                      />
                      <span>{chip.label}</span>
                    </label>
                  );
                })}
              </div>

              <div
                className="clients-filter-group"
                role="group"
                aria-label="Пріоритет"
              >
                <span className="clients-filter-group__title">Пріоритет</span>
                {PRIORITY_CHIPS.map((chip) => {
                  const on = filters.priorities.includes(chip.value);
                  return (
                    <label
                      key={chip.value}
                      className="filter-chip"
                      data-on={on ? "" : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => handlePriorityToggle(chip.value)}
                      />
                      <span>{chip.label}</span>
                    </label>
                  );
                })}
              </div>

              <label className="filter-chip" data-on={filters.overdue ? "" : undefined}>
                <input
                  type="checkbox"
                  checked={filters.overdue}
                  onChange={handleOverdueToggle}
                />
                <span>Лише прострочені</span>
              </label>

              <label className="feature-tasks-list__date-field">
                <span>Дедлайн від</span>
                <input
                  type="datetime-local"
                  value={dueAfterLocal}
                  onChange={handleDueAfterChange}
                  aria-label="Дедлайн від"
                />
              </label>

              <label className="feature-tasks-list__date-field">
                <span>Дедлайн до</span>
                <input
                  type="datetime-local"
                  value={dueBeforeLocal}
                  onChange={handleDueBeforeChange}
                  aria-label="Дедлайн до"
                />
              </label>

              {activeFilters ? (
                <button type="button" className="action-btn" onClick={clearFilters}>
                  Очистити фільтри
                </button>
              ) : null}
            </div>

            {filters.reversedDateRange ? (
              <div
                className="feature-tasks-list__validation"
                role="alert"
              >
                Діапазон дедлайну некоректний: значення «від» пізніше за «до».
              </div>
            ) : null}

            {filters.invalidDateParams ? (
              <div
                className="feature-tasks-list__validation"
                role="alert"
              >
                Некоректне значення дедлайну в URL. Очистіть або виправте параметри
                due_after / due_before.
              </div>
            ) : null}

            {(filters.clientId != null || filters.propertyId != null) ? (
              <div className="clients-toolbar__row clients-toolbar__row--sub">
                <span className="clients-filter-group__title">Контекст</span>
                {filters.clientId != null ? (
                  <ContextChip
                    label={clientContextLabel || `Клієнт #${filters.clientId}`}
                    onRemove={removeClientContext}
                    ariaLabel="Прибрати фільтр клієнта"
                  />
                ) : null}
                {filters.propertyId != null ? (
                  <ContextChip
                    label={propertyContextLabel || `Об'єкт #${filters.propertyId}`}
                    onRemove={removePropertyContext}
                    ariaLabel="Прибрати фільтр об'єкта"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showInitialLoading ? (
        <div className="feature-tasks-list__state">
          <Spinner size="lg" label="Завантаження задач" />
        </div>
      ) : null}

      {showPrimaryError ? (
        <div className="feature-tasks-list__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити задачі"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={handleRetry}
              >
                Повторити
              </button>
            }
          />
        </div>
      ) : null}

      {showUnfilteredEmpty ? (
        <div className="feature-tasks-list__state">
          <EmptyState
            icon="fa-solid fa-list-check"
            title="Задач поки немає"
            description="Тут зʼявлятимуться задачі, які ви створили або які призначені вам."
            size="md"
          />
        </div>
      ) : null}

      {showFilteredEmpty ? (
        <div className="feature-tasks-list__state">
          <EmptyState
            icon="fa-solid fa-filter"
            title="За вибраними фільтрами задач не знайдено"
            description="Змініть фільтри або очистіть їх."
            size="md"
            primaryAction={
              <button type="button" className="action-btn" onClick={clearFilters}>
                Очистити фільтри
              </button>
            }
          />
        </div>
      ) : null}

      {showTable ? (
        <>
          <p className="feature-tasks-list__count" aria-live="polite">
            Всього задач: {pagination.count}
          </p>

          <div
            className="clients-table-wrap feature-tasks-list__table-wrap"
            role="region"
            aria-label="Таблиця задач, горизонтальна прокрутка"
            tabIndex={0}
          >
            <table className="clients-table feature-tasks-list__table">
              <caption className="visually-hidden">Список задач</caption>
              <thead>
                <tr>
                  <th scope="col">Задача</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Пріоритет</th>
                  <th scope="col">Дедлайн</th>
                  <th scope="col">Клієнт</th>
                  <th scope="col">Об&apos;єкт</th>
                  <th scope="col">Відповідальний</th>
                  <th scope="col">Автор</th>
                  <th scope="col">Створено</th>
                  <th scope="col">
                    <span className="visually-hidden">Дії</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const overdue = isTaskOverdue(task);
                  const statusKey = task.status || "todo";
                  const priorityKey = task.priority || "medium";
                  return (
                    <tr
                      key={task.id}
                      className={[
                        "feature-tasks-list__row",
                        overdue ? "feature-tasks-list__row--overdue" : null,
                        statusKey === "done" ? "feature-tasks-list__row--done" : null,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td>
                        <div className="feature-tasks-list__task-cell">
                          <strong
                            className="feature-tasks-list__task-title"
                            title={task.title}
                          >
                            {task.title}
                          </strong>
                          {task.description ? (
                            <p
                              className="feature-tasks-list__task-desc"
                              title={task.description}
                            >
                              {task.description}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span
                          className={[
                            "feature-tasks-list__status-badge",
                            `feature-tasks-list__status-badge--${statusKey}`,
                          ].join(" ")}
                        >
                          {getStatusLabel(statusKey)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={[
                            "feature-tasks-list__priority-badge",
                            `feature-tasks-list__priority-badge--${priorityKey}`,
                          ].join(" ")}
                        >
                          {getPriorityLabel(priorityKey)}
                        </span>
                      </td>
                      <td>
                        {task.due_date ? (
                          <span
                            className={
                              overdue
                                ? "feature-tasks-list__due feature-tasks-list__due--overdue"
                                : "feature-tasks-list__due"
                            }
                          >
                            {overdue ? "Прострочено: " : ""}
                            {formatDateTime(task.due_date)}
                          </span>
                        ) : (
                          "Без дедлайну"
                        )}
                      </td>
                      <td>
                        {task.client ? (
                          <Link
                            to={`/clients/${task.client.id}`}
                            className="feature-tasks-list__link"
                          >
                            {clientDisplayName(task.client, task.client.id)}
                          </Link>
                        ) : (
                          "Без клієнта"
                        )}
                      </td>
                      <td>
                        <PropertySummaryCell
                          propertyId={task.property}
                          summaryEntry={
                            task.property != null
                              ? propertySummaries.get(task.property)
                              : null
                          }
                        />
                      </td>
                      <td>{userDisplayName(task.assigned_to)}</td>
                      <td>{userDisplayName(task.created_by)}</td>
                      <td>{formatCreatedDateUk(task.created_at)}</td>
                      <td className="feature-tasks-list__actions-cell">
                        <TaskRowActionsMenu
                          task={task}
                          menuOpen={openMenuTaskId === task.id}
                          onToggleMenu={handleToggleMenu}
                          onOpenEdit={openEditEditor}
                          onRequestDelete={handleRequestDelete}
                          triggerRef={(node) => setMenuTriggerRef(task.id, node)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <NumberedPagination
            currentPage={filters.page}
            totalPages={totalPagesFromCount(pagination.count, TASK_LIST_PAGE_SIZE)}
            hasPrevious={Boolean(pagination.previous)}
            hasNext={Boolean(pagination.next)}
            onPageChange={goToPage}
            ariaLabel="Пагінація списку задач"
            disabled={refreshing}
          />
        </>
      ) : null}

      <CalendarTaskEditorModal
        open={editorState.open}
        mode={editorState.mode}
        task={editorState.task}
        prefillDay={editorState.mode === "create" ? todayLocalDate() : null}
        propertyLabel={editorState.propertyLabel}
        onClose={closeEditor}
        onSaved={handleEditorSaved}
        mutationGenerationRef={mutationGenerationRef}
      />

      <CalendarTaskDeleteDialog
        open={deleteState.open}
        task={deleteState.task}
        pending={deleteState.pending}
        error={deleteState.error}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}

export default TasksListScreenContent;
